import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryKey,
} from "@tanstack/react-query";
import { useEffect, useState } from "react";

import type { Fraktion } from "@/components/atomic-crm/misc/fraktionen";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";

import { readVoltRoles, type VoltRoles } from "../navigation/voltAreas";
import {
  berlinMonth,
  CLOSING_PENDING,
  monthQueryRange,
} from "./attendanceLogic";
import type {
  AttendanceAudit,
  AttendanceClosing,
  AttendanceEntry,
  AttendanceMember,
  AttendanceSession,
  AttendanceSettings,
} from "./types";

// Reads go through RLS, writes through the RPCs of #13/#15 or RLS on settings and members.

export type SalesPerson = {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  fraktion: Fraktion;
  disabled: boolean;
};

export const salesName = (s: Pick<SalesPerson, "first_name" | "last_name">) =>
  `${s.first_name} ${s.last_name}`.trim();

const sb = () => getSupabaseClient();

const unwrap = <T>({ data, error }: { data: T | null; error: unknown }) => {
  if (error) throw error;
  return data as T;
};

/** Roles from the current session token; ready once the session is known. */
export const useVoltRoles = () => {
  const [state, setState] = useState<{
    roles: VoltRoles | null;
    ready: boolean;
  }>({ roles: null, ready: false });
  useEffect(() => {
    const { data } = sb().auth.onAuthStateChange((_event, session) => {
      setState({ roles: readVoltRoles(session?.access_token), ready: true });
    });
    return () => data.subscription.unsubscribe();
  }, []);
  return state;
};

export const useMonthSessions = (district: Fraktion | null, month: string) =>
  useQuery({
    queryKey: ["attendance", "sessions", district, month],
    enabled: !!district,
    queryFn: async () => {
      const { from, to } = monthQueryRange(month);
      const sessions = unwrap<AttendanceSession[]>(
        await sb()
          .from("attendance_sessions")
          .select("*")
          .eq("district", district)
          .gte("starts_at", from)
          .lt("starts_at", to)
          .order("starts_at"),
      ).filter((s) => berlinMonth(s.starts_at) === month);
      const ids = sessions.map((s) => s.id);
      const entries = ids.length
        ? unwrap<Pick<AttendanceEntry, "session_id" | "status" | "invited">[]>(
            await sb()
              .from("attendance_entries")
              .select("session_id, status, invited")
              .in("session_id", ids),
          )
        : [];
      return { sessions, entries };
    },
  });

/** Last calendar sync run of the district; null if it never ran. */
export const useLastSynced = (district: Fraktion | null) =>
  useQuery({
    queryKey: ["attendance", "synced", district],
    enabled: !!district,
    queryFn: async () =>
      unwrap<{ synced_at: string } | null>(
        await sb()
          .from("attendance_sync_runs")
          .select("synced_at")
          .eq("district", district)
          .maybeSingle(),
      )?.synced_at ?? null,
  });

export const useBillableSession = (
  district: Fraktion | null,
  month: string | null,
) =>
  useQuery({
    queryKey: ["attendance", "billable", district, month],
    enabled: !!district && !!month,
    queryFn: async () =>
      unwrap<number | null>(
        await sb().rpc("attendance_billable_session", {
          district,
          month: `${month}-01`,
        }),
      ),
  });

export const useSessionDetail = (id: number) =>
  useQuery({
    queryKey: ["attendance", "session", id],
    enabled: Number.isSafeInteger(id) && id > 0,
    queryFn: async () => {
      const session = unwrap<AttendanceSession | null>(
        await sb()
          .from("attendance_sessions")
          .select("*")
          .eq("id", id)
          .maybeSingle(),
      );
      if (!session) return null;
      const entries = unwrap<AttendanceEntry[]>(
        await sb()
          .from("attendance_entries")
          .select("*")
          .eq("session_id", id)
          .order("id"),
      );
      const entryIds = entries.map((e) => e.id);
      const scope = entryIds.length
        ? `and(entity.eq.session,entity_id.eq.${id}),and(entity.eq.entry,entity_id.in.(${entryIds.join(",")}))`
        : `and(entity.eq.session,entity_id.eq.${id})`;
      const audit = unwrap<AttendanceAudit[]>(
        await sb()
          .from("attendance_audit")
          .select("*")
          .eq("district", session.district)
          .or(scope)
          .order("at")
          .order("id"),
      );
      const members = unwrap<AttendanceMember[]>(
        await sb()
          .from("attendance_members")
          .select("*")
          .eq("district", session.district),
      );
      const people = new Set([
        ...entries.map((e) => e.sales_id),
        ...audit.flatMap((a) => (a.actor_sales_id ? [a.actor_sales_id] : [])),
      ]);
      const sales = people.size
        ? unwrap<SalesPerson[]>(
            await sb()
              .from("sales")
              .select("id, first_name, last_name, email, fraktion, disabled")
              .in("id", [...people]),
          )
        : [];
      return { session, entries, audit, members, sales };
    },
  });

export const useDistrictSales = (district: Fraktion | null) =>
  useQuery({
    queryKey: ["attendance", "sales", district],
    enabled: !!district,
    queryFn: async () =>
      unwrap<SalesPerson[]>(
        await sb()
          .from("sales")
          .select("id, first_name, last_name, email, fraktion, disabled")
          .eq("fraktion", district)
          .order("last_name")
          .order("first_name"),
      ),
  });

/** The room code is not kept in the persisted query cache. */
export const useRoomCode = (id: number) =>
  useQuery({
    queryKey: ["attendance", "room-code", id],
    enabled: Number.isSafeInteger(id) && id > 0,
    gcTime: 0,
    queryFn: async () =>
      unwrap<string | null>(
        await sb().rpc("attendance_room_code", { session_id: id }),
      ),
  });

export const useAttendanceSettings = (district: Fraktion | null) =>
  useQuery({
    queryKey: ["attendance", "settings", district],
    enabled: !!district,
    queryFn: async () => {
      const settings = unwrap<AttendanceSettings | null>(
        await sb()
          .from("attendance_settings")
          .select("*")
          .eq("district", district)
          .maybeSingle(),
      );
      const members = unwrap<AttendanceMember[]>(
        await sb()
          .from("attendance_members")
          .select("*")
          .eq("district", district),
      );
      return { settings, members };
    },
  });

export const useSignatureUrl = (path: string | null | undefined) =>
  useQuery({
    queryKey: ["attendance", "signature", path],
    enabled: !!path,
    queryFn: async () =>
      unwrap<{ signedUrl: string }>(
        await sb().storage.from("attendance").createSignedUrl(path!, 300),
      ).signedUrl,
  });

/** Newest closing of this session that is still on its way to the BV, or null. */
export const useSessionClosing = (sessionId: number, enabled: boolean) =>
  useQuery({
    queryKey: ["attendance", "session-closing", sessionId],
    enabled: enabled && Number.isSafeInteger(sessionId) && sessionId > 0,
    queryFn: async () =>
      unwrap<Pick<AttendanceClosing, "id" | "status">[]>(
        await sb()
          .from("attendance_closings")
          .select("id, status")
          .contains("session_ids", [sessionId])
          .in("status", CLOSING_PENDING)
          .order("version", { ascending: false })
          .limit(1),
      )[0] ?? null,
  });

const CLOSING_COLUMNS =
  "id, district, month, version, status, session_ids, pdf_big_path, pdf_small_path, review_notified_at, reminded_at, approved_by_name, approved_at, sent_at, created_at";

/** Closing of #17 with its sessions, the entries in its lists and the signatory. */
export const useClosingDetail = (id: number) =>
  useQuery({
    queryKey: ["attendance", "closing", id],
    enabled: Number.isSafeInteger(id) && id > 0,
    queryFn: async () => {
      const closing = unwrap<AttendanceClosing | null>(
        await sb()
          .from("attendance_closings")
          .select(CLOSING_COLUMNS)
          .eq("id", id)
          .maybeSingle(),
      );
      if (!closing) return null;
      const ids = closing.session_ids;
      const sessions = ids.length
        ? unwrap<AttendanceSession[]>(
            await sb()
              .from("attendance_sessions")
              .select("*")
              .in("id", ids)
              .order("starts_at"),
          )
        : [];
      const entries = ids.length
        ? unwrap<Pick<AttendanceEntry, "session_id" | "status" | "invited">[]>(
            await sb()
              .from("attendance_entries")
              .select("session_id, status, invited")
              .in("session_id", ids),
          )
        : [];
      const settings = unwrap<Pick<
        AttendanceSettings,
        "signatory_sales_id" | "signatory_function" | "live"
      > | null>(
        await sb()
          .from("attendance_settings")
          .select("signatory_sales_id, signatory_function, live")
          .eq("district", closing.district)
          .maybeSingle(),
      );
      const signatory = settings?.signatory_sales_id
        ? unwrap<Pick<SalesPerson, "first_name" | "last_name"> | null>(
            await sb()
              .from("sales")
              .select("first_name, last_name")
              .eq("id", settings.signatory_sales_id)
              .maybeSingle(),
          )
        : null;
      return { closing, sessions, entries, settings, signatory };
    },
  });

/** Signed for 10 minutes; a new link is fetched once it is older than 9. */
export const useClosingPdfUrl = (path: string | null | undefined) =>
  useQuery({
    queryKey: ["attendance", "closing-pdf", path],
    enabled: !!path,
    staleTime: 540_000,
    queryFn: async () =>
      unwrap<{ signedUrl: string }>(
        await sb().storage.from("attendance").createSignedUrl(path!, 600),
      ).signedUrl,
  });

/** A mutation that refreshes every attendance query afterwards. */
const useAttendanceMutation = <V, R = unknown>(
  fn: (vars: V) => Promise<R>,
  invalidate: QueryKey = ["attendance"],
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSettled: () => queryClient.invalidateQueries({ queryKey: invalidate }),
  });
};

export const useSetStatus = () =>
  useAttendanceMutation(
    async (v: { entryId: number; status: string; reason: string }) =>
      unwrap(
        await sb().rpc("attendance_set_status", {
          entry_id: v.entryId,
          status: v.status,
          reason: v.reason,
        }),
      ),
  );

export const useDecideLateRequest = () =>
  useAttendanceMutation(
    async (v: {
      entryId: number;
      decision: "confirm" | "reject";
      reason: string;
    }) =>
      unwrap(
        await sb().rpc("attendance_decide_late_request", {
          entry_id: v.entryId,
          decision: v.decision,
          reason: v.reason,
        }),
      ),
  );

export const useAddPerson = () =>
  useAttendanceMutation(
    async (v: {
      sessionId: number;
      salesId: number;
      status: string;
      reason: string;
    }) =>
      unwrap(
        await sb().rpc("attendance_add_person", {
          session_id: v.sessionId,
          sales_id: v.salesId,
          status: v.status,
          reason: v.reason,
        }),
      ),
  );

export const useSetBillable = () =>
  useAttendanceMutation(async (v: { sessionId: number; reason: string }) =>
    unwrap(
      await sb().rpc("attendance_set_billable", {
        session_id: v.sessionId,
        reason: v.reason,
      }),
    ),
  );

export const useSaveSettings = () =>
  useAttendanceMutation(
    async (v: {
      district: Fraktion;
      patch: Partial<
        Pick<
          AttendanceSettings,
          | "bv_recipient"
          | "signatory_sales_id"
          | "signatory_function"
          | "signature_path"
          | "test_recipient"
          | "live"
        >
      >;
    }) =>
      unwrap(
        await sb()
          .from("attendance_settings")
          .update(v.patch)
          .eq("district", v.district)
          .select("district")
          .single(),
      ),
  );

export const useSaveMemberFunction = () =>
  useAttendanceMutation(
    async (v: {
      district: Fraktion;
      salesId: number;
      memberId: number | null;
      fn: string;
    }) =>
      v.memberId
        ? unwrap(
            await sb()
              .from("attendance_members")
              .update({ function: v.fn })
              .eq("id", v.memberId)
              .select("id")
              .single(),
          )
        : unwrap(
            await sb()
              .from("attendance_members")
              .insert({
                district: v.district,
                sales_id: v.salesId,
                function: v.fn,
              })
              .select("id")
              .single(),
          ),
  );

export const useUploadSignature = () =>
  useAttendanceMutation(async (v: { path: string; file: File }) => {
    unwrap(
      await sb()
        .storage.from("attendance")
        .upload(v.path, v.file, { contentType: v.file.type, upsert: false }),
    );
    return v.path;
  });

/** German text for an RPC or RLS error; the codes come from the SQL functions. */
export const attendanceErrorText = (error: unknown) => {
  const message =
    error && typeof error === "object" && "message" in error
      ? String((error as { message: unknown }).message)
      : String(error);
  const known: Record<string, string> = {
    not_allowed: "Dafür fehlen dir die Rechte.",
    reason_required: "Bitte einen Grund angeben.",
    invalid_status: "Unbekannter Status.",
    session_cancelled: "Die Sitzung ist abgesagt.",
    not_small: "Nur kleine Sitzungen gehen einzeln an die BV.",
    not_in_district: "Die Person gehört nicht zu dieser Fraktion.",
    already_listed: "Die Person steht schon auf der Liste.",
    already_decided: "Über diesen Nachtrag ist schon entschieden.",
    already_present: "Die Person hat inzwischen per QR eingecheckt.",
    no_request: "Für diesen Eintrag liegt kein Nachtrag vor.",
    invalid_decision: "Unbekannte Entscheidung.",
    "only a superadmin changes live":
      "Den Livebetrieb schaltet nur ein Superadmin.",
    "is not in district": "Die Person gehört nicht zu dieser Fraktion.",
  };
  const hit = Object.keys(known).find((k) => message.includes(k));
  return hit ? known[hit] : `Fehler: ${message}`;
};
